import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Fab,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { db } from '../config/firebase';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';

function EvaluationOptionsManager() {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentOption, setCurrentOption] = useState({ text: '', category: 'positive', active: true });
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);

  const OPTIONS_COLLECTION = 'avaliacoes_opcoes';

  useEffect(() => {
    loadOptions();
  }, []);

  const loadOptions = async () => {
    try {
      setLoading(true);
      const querySnapshot = await getDocs(collection(db, OPTIONS_COLLECTION));
      const optionsList = [];
      
      querySnapshot.forEach((doc) => {
        optionsList.push({
          id: doc.id,
          ...doc.data(),
        });
      });

      setOptions(optionsList);
    } catch (err) {
      console.error('Erro ao carregar opções:', err);
      setError('Erro ao carregar opções. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (option = null) => {
    if (option) {
      setCurrentOption({
        text: option.text,
        category: option.category,
        active: option.active
      });
      setEditId(option.id);
      setIsEditing(true);
    } else {
      setCurrentOption({ text: '', category: 'positive', active: true });
      setEditId(null);
      setIsEditing(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentOption({ text: '', category: 'positive', active: true });
    setEditId(null);
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentOption(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async () => {
    if (!currentOption.text.trim()) {
      setError('O texto da opção é obrigatório.');
      return;
    }

    try {
      if (isEditing) {
        // Atualizar opção existente
        const optionRef = doc(db, OPTIONS_COLLECTION, editId);
        await updateDoc(optionRef, {
          text: currentOption.text,
          category: currentOption.category,
          active: currentOption.active,
          updatedAt: serverTimestamp()
        });
      } else {
        // Adicionar nova opção
        await addDoc(collection(db, OPTIONS_COLLECTION), {
          text: currentOption.text,
          category: currentOption.category,
          active: currentOption.active,
          createdAt: serverTimestamp()
        });
      }

      handleCloseDialog();
      await loadOptions();
      setError(null);
    } catch (err) {
      console.error('Erro ao salvar opção:', err);
      setError('Erro ao salvar opção. Tente novamente.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Tem certeza que deseja excluir esta opção?')) {
      try {
        const optionRef = doc(db, OPTIONS_COLLECTION, id);
        await deleteDoc(optionRef);
        await loadOptions();
      } catch (err) {
        console.error('Erro ao excluir opção:', err);
        setError('Erro ao excluir opção. Tente novamente.');
      }
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case 'positive':
        return 'Positiva';
      case 'improvement':
        return 'Melhoria';
      default:
        return category;
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'positive':
        return 'success';
      case 'improvement':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <Typography variant="h6">Carregando opções...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            Gerenciamento de Opções de Avaliação
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Adicione, edite ou remova opções que aparecem na avaliação do treinamento
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <Tooltip title="Adicionar nova opção">
            <Fab
              color="primary"
              aria-label="add"
              onClick={() => handleOpenDialog()}
              sx={{ 
                position: 'fixed',
                bottom: 20,
                right: 20,
                zIndex: 1000
              }}
            >
              <AddIcon />
            </Fab>
          </Tooltip>
        </Box>

        <TableContainer component={Paper} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Opção</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Categoria</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {options.map((option) => (
                <TableRow key={option.id}>
                  <TableCell>{option.text}</TableCell>
                  <TableCell>
                    <Chip
                      label={getCategoryLabel(option.category)}
                      color={getCategoryColor(option.category)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={option.active ? 'Ativa' : 'Inativa'}
                      color={option.active ? 'success' : 'default'}
                      size="small"
                      variant={option.active ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(option)}
                      color="primary"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(option.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {options.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h6" color="text.secondary">
              Nenhuma opção de avaliação cadastrada
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Clique no botão + para adicionar sua primeira opção
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Diálogo para adicionar/editar opções */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {isEditing ? 'Editar Opção' : 'Adicionar Nova Opção'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {isEditing 
              ? 'Edite os detalhes da opção de avaliação' 
              : 'Adicione uma nova opção que aparecerá na avaliação do treinamento'}
          </DialogContentText>
          
          <TextField
            autoFocus
            margin="dense"
            name="text"
            label="Texto da Opção"
            type="text"
            fullWidth
            variant="outlined"
            value={currentOption.text}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel>Categoria</InputLabel>
                <Select
                  name="category"
                  value={currentOption.category}
                  onChange={handleInputChange}
                  label="Categoria"
                >
                  <MenuItem value="positive">Positiva</MenuItem>
                  <MenuItem value="improvement">Melhoria</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  name="active"
                  value={currentOption.active}
                  onChange={(e) => setCurrentOption(prev => ({
                    ...prev,
                    active: e.target.value === 'true' || e.target.value === true
                  }))}
                  label="Status"
                >
                  <MenuItem value={true}>Ativa</MenuItem>
                  <MenuItem value={false}>Inativa</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            startIcon={<SaveIcon />}
            disabled={!currentOption.text.trim()}
          >
            {isEditing ? 'Atualizar' : 'Adicionar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EvaluationOptionsManager;